import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { coldLandingOpen, consumeColdLanding, phoneViewport } from './coldLanding';
import { eventLanding, type EventLanding } from './eventMode';
import { readRecentEvent } from './recentEvent';
import { useEventModeData } from './useEventMode';

// After this a late answer never moves the rep off home.
const LANDING_WAIT_MS = 3000;

// Wraps the home route and always shows home straight away. On a phone's first landing on `/`
// it asks in the background which live event the rep works (several: the one opened last, else the
// most specific) and moves there, unless the rep has touched the page or the window has passed.
export function EventModeLanding({ children }: { children: ReactNode }) {
  const location = useLocation();
  // Laptops and tablets never qualify, so they never ask about events.
  const [cold] = useState(() => coldLandingOpen() && phoneViewport());
  const { pending, input } = useEventModeData(cold);
  // A tap, a key or the cutoff ends the window; leaving `/` unmounts this and ends it too.
  const [expired, setExpired] = useState(!cold);
  // Settled once, then frozen: a refetch after the answer never turns into a redirect.
  const [settled, setSettled] = useState<NonNullable<EventLanding> | 'home' | undefined>(cold ? undefined : 'home');
  if (settled === undefined && (expired || !pending)) {
    const landing = !expired && input
      ? eventLanding({ ...input, recentEventId: readRecentEvent(input.viewer.id, input.now, Infinity)?.id, coldLanding: true, phone: true, pathname: location.pathname, search: location.search })
      : null;
    setSettled(landing ?? 'home');
  }

  useEffect(() => {
    if (!cold) return;
    const stop = () => setExpired(true);
    const timer = window.setTimeout(stop, LANDING_WAIT_MS);
    window.addEventListener('pointerdown', stop, true);
    window.addEventListener('keydown', stop, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', stop, true);
      window.removeEventListener('keydown', stop, true);
    };
  }, [cold]);
  // One shot per page load, decided or abandoned: coming back to home later never redirects.
  useEffect(() => { if (settled !== undefined) consumeColdLanding(); }, [settled]);
  useEffect(() => () => consumeColdLanding(), []);

  return <>
    {children}
    {settled !== undefined && settled !== 'home' && !expired && <Navigate to={settled.to} replace />}
  </>;
}
